import "./static/AccessManagement.scss";
import * as React from "react";
import { AsyncOperationsModel } from "../../../utility/AsyncOperationsModel";
import { IServicesProvider } from "../../../di/IServicesProvider";
import { AccessManagementOperations } 
from "../../../utility/admin/OrganisationOperations/AccessManagementOperations/AccessManagementOperations";
import { observable, computed, action, runInAction } from "mobx";
import { IOrganisation, IAccessCode, IEmailHost, IRole, ILoginCode } from "../../../model/ModelTypes";
import { BaseAdminOperations } from "../../../utility/admin/BaseAdminOperations";
import { IConfig } from "../../../IConfig";
import { observer } from "mobx-react";
import BaseComponent from "../../../utility/BaseComponent";
import { Card, CardText, Paper, TextField,
    DataTable, TableHeader, TableRow, TableColumn, 
    TableBody, Button, CircularProgress, SelectField, Chip, DialogContainer } from "react-md";
import { isNullOrUndefined } from "util";
import * as moment from 'moment';

const rootConfig: IConfig = require("Config"); 
export class AccessManagementModel extends AsyncOperationsModel {
    servicesProvider: IServicesProvider;
    operations: AccessManagementOperations;
    @observable organisation: IOrganisation;
    @observable selectedAccessCodeRoleId: string = "";
    @observable selectedEmailHostRoleId: string = "";
    @observable selectedLoginCodeRoleId: string = "";
    @observable typedEmailHost: string = "";
    @observable deleteDialogVisible: boolean = false;
    private onDeleteConfirmedFunction: Function;
    @computed get accessCodes(): IAccessCode[] {
        return this.organisation.access_codes.sort( (item, item1) => item.role_id.localeCompare(item1.role_id));
    }
    @computed get loginCodes(): ILoginCode[] {
        return this.organisation.loginCodes.sort( (item, item1) => item.roleId.localeCompare(item1.roleId));
    }
    @computed get generateAccessCodeEnabled() {
        return !isNullOrUndefined(this.selectedAccessCodeRoleId) && this.selectedAccessCodeRoleId !== "";
    }
    @computed get generateLoginCodeEnabled() {
        return !isNullOrUndefined(this.selectedLoginCodeRoleId) && this.selectedLoginCodeRoleId !== "";
    } 
    @computed get generateEmailPatternEnabled() {
        return !isNullOrUndefined(this.selectedEmailHostRoleId) && this.selectedEmailHostRoleId !== ""
        && !isNullOrUndefined(this.typedEmailHost) && this.typedEmailHost !== "";
    }
    @computed get shouldShowManageLoginCodes() {
        return isNullOrUndefined(this.organisation.allowReadOnlyAccess) ? false : this.organisation.allowReadOnlyAccess;
    }
    @computed get emailHosts(): IEmailHost[] {
        let output: IEmailHost[] = [];
        output = output.concat(
            this.organisation.email_hosts
            .filter( item => !isNullOrUndefined(item.role_ids) && item.role_ids.length === 1)
        ); 
        this.organisation.email_hosts
        .filter( item => !isNullOrUndefined(item.role_ids) &&  item.role_ids.length > 1)
        .forEach(
            item => {
                item.role_ids.forEach(
                    roleId => {
                        output.push(
                            {
                                host: item.host,
                                role_ids: [roleId]
                            }
                        );
                    }
                );
            }
        );
        return output.sort( (item, item1) => item.role_ids[0].localeCompare(item1.role_ids[0]));
    }
    @computed get roles(): IRole[] {
        return this.organisation.roles.sort( (item, item1) => item.name.localeCompare(item1.name));
    }
    @action.bound
    updateOrganisation(organisation: IOrganisation) {
        this.organisation = organisation;
        
    }
    @action.bound
    setOperations(operations: AccessManagementOperations) {
        this.operations = operations;
        this.incrementAsyncOperations();
        this.operations.observeOrganisationChanges()
        .subscribe(
            recievedOrganisation => {
                this.updateOrganisation(recievedOrganisation);
                this.decrementAsyncOperations();
            },
            error => {
                this.setErrorMessages(["Failed to fetch organisation details"]);
                this.decrementAsyncOperations();
            }
        );
    }
    @action.bound
    resetAsyncOperations() {
        this.asyncOperationsCount = 0;
    }
    @action.bound
    prepare() {
        this.resetAsyncOperations();
        this.incrementAsyncOperations();
        BaseAdminOperations.build<AccessManagementOperations>(
            AccessManagementOperations,
            this.servicesProvider.getDataStore(),
            this.servicesProvider.getAdminServicesProvider()
            .getUsersControls().getOperations().getApi(),
            this.servicesProvider.getUser().organisation,
            this.servicesProvider.getUser(),
            rootConfig)
            .first()
            .subscribe(
                recievedOperations => {
                    runInAction( () => {
                        this.setOperations(recievedOperations);
                        this.decrementAsyncOperations();
                    });
                },
                error => {
                    this.setErrorMessages(["Something went wrong"]);
                    this.decrementAsyncOperations();
                }
            );
    }
    @action.bound
    onDeleteConfirmed() {
        if (this.onDeleteConfirmedFunction != null) {
            this.onDeleteConfirmedFunction();
            this.deleteDialogVisible = false;
            this.onDeleteConfirmedFunction = null;
        }
    }
    @action.bound
    onDeleteAccessCodeClicked(accessCode: IAccessCode) {
        this.deleteDialogVisible = true;
        this.onDeleteConfirmedFunction = () => this.operations.deleteItem(accessCode.role_id).subscribe();
    }
    @action.bound
    onDeleteLoginCodeClicked(loginCode: ILoginCode) {
        this.deleteDialogVisible = true;
        this.onDeleteConfirmedFunction = () => this.operations
        .deleteItem(undefined, undefined, loginCode.roleId).subscribe();
    }
    @action.bound
    onAddAccessCodeClicked() {
        this.operations.addItem(this.selectedAccessCodeRoleId).subscribe();
    }
    @action.bound
    onAddLoginCodeClicked() {
        this.operations.addItem(undefined, undefined, this.selectedLoginCodeRoleId).subscribe();
    }
    @action.bound
    onDeleteEmailHostClicked(emailHost: IEmailHost) {
        this.deleteDialogVisible = true;
        this.onDeleteConfirmedFunction = () => 
        this.operations.deleteItem(undefined, {email_host: emailHost.host, role_id: emailHost.role_ids[0]}).subscribe();
    }
    @action.bound
    onAddEmailHostClicked() {
        this.operations
        .addItem(undefined, {role_id: this.selectedEmailHostRoleId, email_host: this.typedEmailHost}).subscribe();
        this.typedEmailHost = "";
    }
    constructor(servicesProvider: IServicesProvider) {
        super();
        this.servicesProvider = servicesProvider;
    }
}
@observer class AccessManagementView extends BaseComponent<{model: AccessManagementModel}, {}> {

    getExpiredColumn(isoDate: string) {
        let expiryDate = moment(isoDate).toDate();
        if (expiryDate.getTime() < moment().toDate().getTime()) {
            return (
                    <Chip 
                        label="Expired" 
                        labelStyle={
                            {
                                color: "white"
                            }
                        }
                        style={
                            {
                                backgroundColor: "#FF5C5C"                           
                            }
                        } 
                    />
                    );
        } else {
            return <p>{expiryDate.toLocaleDateString()}</p>;
        }
    }
    getExpiredAccessCodesCount() {
        let count = 0;
        if (!isNullOrUndefined(this.props.model.organisation) && 
        !isNullOrUndefined(this.props.model.organisation.access_codes) ) {
            this.props.model.organisation.access_codes.map(item => item.expiry_date).forEach(
                isoDate => {
                    let expiryDate = moment(isoDate).toDate();
                    if (expiryDate.getTime() < moment().toDate().getTime()) {
                        count += 1;
                    } 
                }
            );    
        }
        return count;
    }

    render() {
        let expiredAccessCodesCount = this.getExpiredAccessCodesCount();

        return(
            <div className="AccessManagementRoot GeneralViewContainer">
                {
                    this.props.model.errorMessages.length > 0 &&
                    <Card className="messagesCard">
                        <CardText className="messageText">
                            {
                                this.props.model.errorMessages.map(
                                    (item, index) => {
                                        return <p key={index}>{item}</p>;
                                    }
                                )
                            }
                        </CardText>
                    </Card>

                }
                {
                    isNullOrUndefined(this.props.model.organisation) &&
                    <CircularProgress centered={true} id="progress" />
                }
                {
                    !isNullOrUndefined(this.props.model.organisation) &&
                    !isNullOrUndefined(this.props.model.organisation.allowReadOnlyAccess) &&
                    this.props.model.organisation.allowReadOnlyAccess &&
                        <div className="AccessManagementPaperContainer">
                    <div className="AccessManagementPaperContainer" >
                    <h2>Entry Codes</h2>
                    <Paper
                        id="AccessManagementAccessCodesPaper"
                        className="AccessManagementPaper"
                        zDepth={2}
                    >
                        <div
                            className="AccessManagementDataTableContainer"
                        >
                            <DataTable
                                baseId="accessCodeTable"
                                className='AccessCodeTableContainer'
                            >
                                <TableHeader>
                                    <TableRow selectable={false}>
                                        <TableColumn>Role</TableColumn>
                                        <TableColumn>Entry Code</TableColumn>
                                        <TableColumn>QR Code</TableColumn>
                                        {/* <TableColumn>Actions</TableColumn> */}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {
                                        this.props.model.loginCodes &&
                                            this.props.model.loginCodes instanceof Array  &&
                                        this.props.model.loginCodes.map(
                                            (loginCode, index) => {

                                                return (
                                                <TableRow key={index} selectable={false} >
                                                    <TableColumn>
                                                    {
                                                        this.props.model.roles.find(
                                                            item => item.id === loginCode.roleId
                                                        ).name
                                                    }
                                                    </TableColumn>
                                                    <TableColumn>{loginCode.code}</TableColumn>
                                                    <TableColumn><b><a href={loginCode.qrCodeLink} target="blank">
                                                        Click to download
                                                    </a></b></TableColumn>
                                                    {/* <TableColumn>
                                                        <div>
                                                            <Button 
                                                                flat={true} 
                                                                onClick={
                                                                () => 
                                                                this.
                                                                props.
                                                                model.
                                                                onDeleteLoginCodeClicked(loginCode)
                                                                }
                                                            >
                                                            Delete
                                                            </Button>
                                                        </div>
                                                    </TableColumn> */}
                                                </TableRow>
                                                );
                                            }
                                        )
                                    }
                                </TableBody>
                            </DataTable>
                            {/* <div className="AccessManagementAction">
                            <SelectField
                                id="accessCodesRoles"
                                label="Roles"
                                placeholder="Select A Role"
                                required={true}
                                menuItems={
                                    this.props.model.roles.map(item => {
                                            return {label: item.name, value: item.id};
                                    })
                                }
                                value={this.props.model.selectedLoginCodeRoleId}
                                onChange={(value) => this.props.model.selectedLoginCodeRoleId = (value.toString())}
                            />
                            <div className="GenerateAccessCodeButton">
                                <Button
                                    secondary={true}
                                    disabled={!this.props.model.generateLoginCodeEnabled}
                                    raised={true}
                                    onClick={this.props.model.onAddLoginCodeClicked}
                                >
                                    Generate
                                </Button>
                            </div>
                            </div> */}
                        </div>
                    </Paper>
                </div>
                            <div className="AccessManagementPaperContainer">
                                <h2>Group Chat Pin Codes</h2>
                                <Paper
                                    id="GroupChatPinCodesPaper"
                                    className="GroupChatPinCodesPaperContainer"
                                    zDepth={2}
                                >
                                    <div
                                        className="AccessManagementDataTableContainer"
                                    >
                                        <DataTable
                                            baseId="GroupChatPinCodesTable"
                                            className='AccessCodeTableContainer'
                                        >
                                            <TableHeader>
                                                <TableRow selectable={false}>
                                                    <TableColumn>Code</TableColumn>
                                                    <TableColumn>Created By</TableColumn>
                                                    <TableColumn>Identifier</TableColumn>
                                                     <TableColumn>Time Created</TableColumn>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {
                                                    this.props.model.organisation.group_chat_pin_codes &&
                                                    this.props.model.organisation.group_chat_pin_codes instanceof Array
                                                    && this.props.model.organisation.group_chat_pin_codes.map(
                                                        (pinCode, index) => {

                                                            return (
                                                                <TableRow key={pinCode.code} selectable={false} >
                                                                    <TableColumn>{pinCode.code}</TableColumn>
                                                                    <TableColumn>{pinCode.username}</TableColumn>
                                                                    <TableColumn>{pinCode.identifier}</TableColumn>
                                                                    <TableColumn>
                                                                        {moment(pinCode.created_time)
                                                                        .format("ddd DD-MMM-YYYY")}
                                                                    </TableColumn>

                                                                    {/* <TableColumn>
                                                        <div>
                                                            <Button
                                                                flat={true}
                                                                onClick={
                                                                () =>
                                                                this.
                                                                props.
                                                                model.
                                                                onDeleteLoginCodeClicked(loginCode)
                                                                }
                                                            >
                                                            Delete
                                                            </Button>
                                                        </div>
                                                    </TableColumn> */}
                                                                </TableRow>
                                                            );
                                                        }
                                                    )
                                                }
                                            </TableBody>
                                        </DataTable>
                                        {/* <div className="AccessManagementAction">
                            <SelectField
                                id="accessCodesRoles"
                                label="Roles"
                                placeholder="Select A Role"
                                required={true}
                                menuItems={
                                    this.props.model.roles.map(item => {
                                            return {label: item.name, value: item.id};
                                    })
                                }
                                value={this.props.model.selectedLoginCodeRoleId}
                                onChange={(value) => this.props.model.selectedLoginCodeRoleId = (value.toString())}
                            />
                            <div className="GenerateAccessCodeButton">
                                <Button
                                    secondary={true}
                                    disabled={!this.props.model.generateLoginCodeEnabled}
                                    raised={true}
                                    onClick={this.props.model.onAddLoginCodeClicked}
                                >
                                    Generate
                                </Button>
                            </div>
                            </div> */}
                                    </div>
                                </Paper>
                            </div>
                        </div>
                }
                {
                    !isNullOrUndefined(this.props.model.organisation) &&
                    this.props.model.organisation.parent_id !== "3" &&
                    <div className="AccessManagementPaperContainer">
                    <h2>Manage Access Code</h2>
                    {
                        expiredAccessCodesCount > 0 &&
                        <p className='ExpiredAccessCodesNotification'> 
                            You have {
                            expiredAccessCodesCount
                            } expired acccess code{expiredAccessCodesCount > 1 ? "s" : ""} 
                        </p>
                    }
                    <Paper
                        id="AccessManagementAccessCodesPaper"
                        className="AccessManagementPaper"
                        zDepth={2}
                    >
                        <div
                            className="AccessManagementDataTableContainer"
                        >
                            <DataTable
                                baseId="accessCodeTable"
                                className='AccessCodeTableContainer'
                            >
                                <TableHeader>
                                    <TableRow selectable={false}>
                                        <TableColumn>Role</TableColumn>
                                        <TableColumn>Access Code</TableColumn>
                                        <TableColumn>Expiry Date</TableColumn>
                                        <TableColumn>Actions</TableColumn>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {
                                        this.props.model.accessCodes.map(
                                            (accessCode, index) => {

                                                return (
                                                <TableRow key={index} selectable={false} >
                                                    <TableColumn>
                                                    {
                                                        this.props.model.roles.find(
                                                            item => item.id === accessCode.role_id
                                                        ).name
                                                    }
                                                    </TableColumn>
                                                    <TableColumn>{accessCode.access_code}</TableColumn>
                                                    <TableColumn>
                                                    {this.getExpiredColumn(accessCode.expiry_date)}
                                                    </TableColumn>
                                                    <TableColumn>
                                                        <div>
                                                            <Button 
                                                                flat={true} 
                                                                onClick={
                                                                () => 
                                                                this.
                                                                props.
                                                                model.
                                                                onDeleteAccessCodeClicked(accessCode)
                                                                }
                                                            >
                                                            Delete
                                                            </Button>
                                                        </div>
                                                    </TableColumn>
                                                </TableRow>
                                                );
                                            }
                                        )
                                    }
                                </TableBody>
                            </DataTable>
                            <div className="AccessManagementAction">
                            <SelectField
                                id="accessCodesRoles"
                                label="Roles"
                                placeholder="Select A Role"
                                required={true}
                                menuItems={
                                    this.props.model.roles.map(item => {
                                            return {label: item.name, value: item.id};
                                    })
                                }
                                value={this.props.model.selectedAccessCodeRoleId}
                                onChange={(value) => this.props.model.selectedAccessCodeRoleId = (value.toString())}
                            />
                            <div className="GenerateAccessCodeButton">
                                <Button
                                    secondary={true}
                                    disabled={!this.props.model.generateAccessCodeEnabled}
                                    raised={true}
                                    onClick={this.props.model.onAddAccessCodeClicked}
                                >
                                    Generate
                                </Button>
                                <p>Access codes are valid for 30 days</p>
                            </div>
                            </div>
                        </div>
                    </Paper>
                </div>
                }
                
                {
                    !isNullOrUndefined(this.props.model.organisation) &&
                    this.props.model.organisation.parent_id !== "3" &&
                    <div className="AccessManagementPaperContainer">
                    <h2>Manage Email Hosts</h2>
                    <Paper
                        id="AccessManagementEmailHostPaper"
                        className="AccessManagementPaper"
                        zDepth={2}
                    >
                        <div className='AccessManagementDataTableContainer'>
                            <DataTable
                                baseId="emailHostsTable"
                                className='AccessEmailHostsTableContainer'
                            >
                                <TableHeader>
                                    <TableRow selectable={false}>
                                        <TableColumn>Role</TableColumn>
                                        <TableColumn>Email Host</TableColumn>
                                        <TableColumn>Actions</TableColumn>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {
                                        this.props.model.emailHosts.map(
                                            (emailHost, index) => {
                                                return (
                                                <TableRow key={index} selectable={false} >
                                                    <TableColumn>
                                                    {
                                                        this.props.model.roles.find(
                                                            item => item.id === emailHost.role_ids[0]
                                                        ).name
                                                    }
                                                    </TableColumn>
                                                    <TableColumn>{emailHost.host}</TableColumn>
                                                    <TableColumn>
                                                        <Button
                                                            flat={true}
                                                            onClick={
                                                                () => this.
                                                                props.model.onDeleteEmailHostClicked(emailHost)
                                                            }
                                                        >
                                                        Delete
                                                        </Button>
                                                    </TableColumn>
                                                </TableRow>
                                                );
                                            }
                                        )
                                    }
                                </TableBody>
                            </DataTable>
                            <div className='AccessManagementAction EmailHostsActionContainer'>
                                <div className="EmailHostsInputContainer">
                                    <SelectField
                                        id="emailHostsRoles"
                                        label="Roles"
                                        placeholder="Select A Role"
                                        required={true}
                                        menuItems={
                                            this.props.model.roles.map(item => {
                                                    return {label: item.name, value: item.id};
                                            })
                                        }
                                        value={this.props.model.selectedEmailHostRoleId}
                                        onChange={(value) => this.
                                        props.model.selectedEmailHostRoleId = (value.toString())}
                                    />
                                    <div className="EmailHostsTextContainer">
                                        <h1>@</h1> 
                                        <TextField
                                            id="emailHostInput"
                                            helpText="The ending of an email.
                                            For example, role@example.com; the host will be example.com"
                                            required={true}
                                            className="stackableTextInputRight"
                                            fullWidth={false}
                                            label="Email Host" 
                                            type="text" 
                                            onChange={(e) => this.props.model.typedEmailHost = ( e.toString())}
                                            value={this.props.model.typedEmailHost}
                                        />  
                                    </div>
                                </div>
                                <Button
                                    secondary={true}
                                    disabled={!this.props.model.generateEmailPatternEnabled}
                                    raised={true}
                                    onClick={this.props.model.onAddEmailHostClicked}
                                >
                                    Generate
                                </Button>
                            </div>
                        </div>
                    </Paper>
                    <DialogContainer
                        id="simple-action-dialog"
                        visible={this.props.model.deleteDialogVisible}
                        onHide={() => this.props.model.deleteDialogVisible = false}
                        title="Confrim Delete"
                        actions={[
                            <Button
                                key="cancel"
                                flat={true}
                                onClick={() => this.props.model.deleteDialogVisible = false}
                                className='CancelDelete'
                            >
                            Cancel
                            </Button>,
                            <Button
                                key="delete"
                                flat={true}
                                onClick={
                                    () => this.props.model.onDeleteConfirmed()   
                                }
                                className='ConfirmDelete'
                            >
                            Delete
                            </Button>

                        ]}
                        className='DeleteConfirmation'
                    >
                    <p> Are you sure you want to delete this item?</p>
                        
                    </DialogContainer>
                </div>
                }
                
            </div>
        );
    }
}
export default AccessManagementView;


// WEBPACK FOOTER //
// ./src/view/Admin/AccessManagement/AccessManagement.tsx